import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReportsData } from "@/hooks/useReportsData";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { format } from "date-fns";
import { TrendingUp, Clock, Star } from "lucide-react";

const FlashcardsIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 256 256"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M180,72H36A20,20,0,0,0,16,92V204a20,20,0,0,0,20,20H180a20,20,0,0,0,20-20V92A20,20,0,0,0,180,72Zm-4,128H40V96H176ZM240,52V176a12,12,0,0,1-24,0V56H64a12,12,0,0,1,0-24H220A20,20,0,0,1,240,52Z"
    />
  </svg>
);

interface FlashcardsAnalyticsProps {
  data: ReportsData;
  compact?: boolean;
}

const MASTERY_COLORS = {
  mastered: "hsl(var(--chart-2))",
  learning: "hsl(var(--chart-3))",
  notStudied: "hsl(var(--chart-5))",
};

export const FlashcardsAnalytics: React.FC<FlashcardsAnalyticsProps> = ({
  data,
  compact = false,
}) => {
  const totalCards = data.flashcardDecks.reduce(
    (sum, deck) => sum + deck.total_cards,
    0
  );
  const totalMastered = data.flashcardDecks.reduce(
    (sum, deck) => sum + deck.mastered_count,
    0
  );
  const totalLearning = data.flashcardDecks.reduce(
    (sum, deck) => sum + deck.learning_count,
    0
  );
  const totalNotStudied = data.flashcardDecks.reduce(
    (sum, deck) => sum + deck.not_studied_count,
    0
  );

  const masteryData = [
    { name: "Mastered", value: totalMastered, color: MASTERY_COLORS.mastered },
    { name: "Learning", value: totalLearning, color: MASTERY_COLORS.learning },
    {
      name: "Not Studied",
      value: totalNotStudied,
      color: MASTERY_COLORS.notStudied,
    },
  ].filter((d) => d.value > 0);

  const studyTrendData = data.studySessions.map((session) => ({
    date: format(new Date(session.date), "MMM d"),
    studied: session.studied_count,
    due: session.due_count,
  }));

  // Calculate retention rate from reviews
  const goodReviews = data.flashcardReviews
    .filter((r) => r.rating >= 3)
    .reduce((sum, r) => sum + r.count, 0);
  const totalReviews = data.flashcardReviews.reduce(
    (sum, r) => sum + r.count,
    0
  );
  const retentionRate =
    totalReviews > 0 ? Math.round((goodReviews / totalReviews) * 100) : 0;

  const chartConfig = {
    studied: { label: "Studied", color: "hsl(var(--chart-1))" },
    due: { label: "Due", color: "hsl(var(--chart-5))" },
  };

  if (totalCards === 0) {
    return (
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FlashcardsIcon className="h-5 w-5" />
            Flashcards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-48 items-center justify-center text-center">
            <div>
              <FlashcardsIcon className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                No flashcard decks yet. Start studying to see your progress!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FlashcardsIcon className="h-5 w-5" />
            Flashcards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-6">
            {/* Mastery pie chart */}
            <div className="h-32 w-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={masteryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {masteryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Stats */}
            <div className="flex-1 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Mastered</span>
                <span className="font-medium text-foreground">
                  {totalMastered}/{totalCards}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Learning</span>
                <span className="font-medium text-foreground">
                  {totalLearning}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Retention Rate
                </span>
                <span className="font-medium text-foreground">
                  {retentionRate}%
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <FlashcardsIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Cards</p>
                <p className="text-xl font-semibold">{totalCards}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-correct/10 p-2">
                <Star className="h-5 w-5 text-correct" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Mastered</p>
                <p className="text-xl font-semibold">{totalMastered}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange/10 p-2">
                <Clock className="h-5 w-5 text-orange" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Due Today</p>
                <p className="text-xl font-semibold">{data.totalCardsDue}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-info/10 p-2">
                <TrendingUp className="h-5 w-5 text-info" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Retention Rate</p>
                <p className="text-xl font-semibold">{retentionRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Mastery Breakdown */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Mastery Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div className="h-40 w-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={masteryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {masteryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {masteryData.map((item) => (
                  <div key={item.name} className="flex items-center gap-3">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="flex-1 text-sm text-muted-foreground">
                      {item.name}
                    </span>
                    <span className="font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Study Trend */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Study Trend (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {studyTrendData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-40 w-full">
                <AreaChart data={studyTrendData}>
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="studied"
                    stroke="hsl(var(--chart-1))"
                    fill="hsl(var(--chart-1))"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            ) : (
              <div className="flex h-40 items-center justify-center">
                <p className="text-sm text-muted-foreground">
                  No study sessions recorded yet
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Decks Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Deck Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Deck Name
                  </th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Total Cards
                  </th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Mastered
                  </th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Learning
                  </th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Progress
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.flashcardDecks.map((deck) => {
                  const progress =
                    deck.total_cards > 0
                      ? Math.round((deck.mastered_count / deck.total_cards) * 100)
                      : 0;
                  return (
                    <tr key={deck.id} className="border-b border-border/50">
                      <td className="py-3 font-medium">{deck.title}</td>
                      <td className="py-3 text-muted-foreground">
                        {deck.total_cards}
                      </td>
                      <td className="py-3 text-correct">{deck.mastered_count}</td>
                      <td className="py-3 text-orange">{deck.learning_count}</td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-correct"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {progress}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
